import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, FileText, Send, ShieldAlert, Upload } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import CommentThread from "../components/CommentThread.jsx";
import SomContextBanner from "../components/SomContextBanner.jsx";
import SomFloatingShortcut from "../components/SomFloatingShortcut.jsx";
import ProgressiveStatusControl from "../components/ProgressiveStatusControl.jsx";
import TasksTab from "../components/TasksTab.jsx";
import NewPartsRequestDialog from "../components/NewPartsRequestDialog.jsx";


const STATUS_COLORS = {
  "Incompleto": "bg-[#E2E8F0] text-[#495057]",
  "Evidências em recolha": "bg-[#FFF3CD] text-[#856404]",
  "Submetido à marca": "bg-[#E0E7FF] text-[#1E3A8A]",
  "Aguarda resposta da marca": "bg-[#FFF3CD] text-[#856404]",
  "Informação adicional solicitada": "bg-[#FFE4B5] text-[#7C5400]",
  "Aprovado": "bg-[#D4EDDA] text-[#155724]",
  "Não aprovado": "bg-[#F8D7DA] text-[#721C24]",
  "Reparação autorizada": "bg-[#CCE5FF] text-[#004085]",
  "Reparação concluída": "bg-[#B8DAFF] text-[#004085]",
  "Faturado": "bg-[#D1ECF1] text-[#0C5460]",
  "Encerrado": "bg-[#E2E8F0] text-[#495057]",
  "Cancelado": "bg-[#F8D7DA] text-[#721C24]",
};

const TABS = [
  { key: "overview", label: "Resumo" },
  { key: "tasks", label: "Tarefas & Contactos" },
  { key: "comments", label: "Comentários & Anexos" },
  { key: "audit", label: "Registo de auditoria" },
];

export default function WarrantyClaimDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSomId = location.state?.fromSomId || null;
  const [swc, setSwc] = useState(null);
  const [users, setUsers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("overview");
  const [partsDecision, setPartsDecision] = useState(null); // { swc_id, service_case_id }
  const [showCreatePec, setShowCreatePec] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/warranty-claims/${id}`);
      setSwc(data);
      setNotFound(false);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    reload();
    api.get("/users").then((r) => setUsers(r.data));
  }, [id, reload]);

  const back = () => {
    if (fromSomId) navigate(`/service-cases/${fromSomId}`);
    else navigate(`/warranty-claims`);
  };

  if (notFound) {
    return (
      <div className="p-6 text-sm">
        <div className="text-[#DC3545] mb-3">Reclamação de garantia não encontrada.</div>
        <Link to="/warranty-claims" className="text-[#1F3864] hover:underline">← Voltar à lista</Link>
      </div>
    );
  }
  if (!swc) return <div className="p-6 text-sm text-[#6C757D]">A carregar…</div>;

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Sticky header */}
      <div
        className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm"
        data-testid="swc-detail-sticky-header"
      >
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <button
            onClick={back}
            className="inline-flex items-center gap-1 hover:text-[#1F3864]"
            data-testid="swc-detail-back"
          >
            <ChevronLeft className="w-3 h-3" />
            {fromSomId ? `Voltar à obra ${swc.service_case_number || ""}`.trim() : "Garantias"}
          </button>
          <span>/</span>
          <span className="font-mono text-[11px] text-[#1A1A2E]" data-testid="swc-detail-breadcrumb">
            {swc.number}
          </span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1
                className="font-heading text-xl font-bold text-[#1A1A2E] flex items-center gap-1.5"
                data-testid="swc-detail-number"
              >
                <ShieldAlert className="w-5 h-5 text-[#1F3864]" />
                <span className="font-mono">{swc.number}</span>
              </h1>
              <span
                className="inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#E0E7FF] text-[#1E3A8A]"
                data-testid="swc-detail-type-badge"
              >
                {swc.claim_type}
              </span>
              <span
                className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                  STATUS_COLORS[swc.status] || "bg-[#E2E8F0] text-[#495057]"
                }`}
                data-testid="swc-detail-status-badge"
              >
                {swc.status}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6C757D]">
              <span>Marca: {swc.brand || "—"}</span>
              {swc.customer_name && <span>· {swc.customer_name}</span>}
              {swc.vehicle_registration && (
                <span className="font-mono">· {swc.vehicle_registration}</span>
              )}
              <span data-testid="swc-detail-updated">· Atualizado {fmtDateTime(swc.updated_at)}</span>
            </div>
          </div>
        </div>

        {/* Progressive status control */}
        <div className="mt-3">
          <ProgressiveStatusControl
            entity="swc"
            recordId={id}
            currentStatus={swc.status}
            onAdvance={async (newStatus, comment) => {
              const { data } = await api.patch(`/warranty-claims/${id}`, { status: newStatus, comment });
              await reload();
              if (data?.decision_prompt?.kind === "warranty_parts") {
                setPartsDecision(data.decision_prompt);
              }
            }}
          />
        </div>

        {/* Tabs */}
        <div className="mt-3 border-b -mb-3 border-gray-200 flex gap-5 overflow-x-auto">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              data-testid={`swc-detail-tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === tb.key
                  ? "border-[#1F3864] text-[#1F3864]"
                  : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {tab === "overview" && <OverviewTab swc={swc} reload={reload} />}
        {tab === "tasks" && (
          <TasksTab
            caseId={swc.service_case_id}
            linkedModule="warranty_claim"
            linkedRecordId={swc.id}
            users={users}
          />
        )}
        {tab === "comments" && (
          <div className="max-w-3xl space-y-4">
            <EvidencePanel swc={swc} onChange={reload} />
            <CommentThread recordType="warranty_claim" recordId={id} />
          </div>
        )}
        {tab === "audit" && <AuditTab recordType="warranty_claim" recordId={id} />}
      </div>

      {swc.service_case_id && (
        <SomFloatingShortcut caseId={swc.service_case_id} currentModule="swc" />
      )}

      {partsDecision && (
        <WarrantyPartsDecisionDialog
          onYes={async () => {
            const { data } = await api.post(`/warranty-claims/${id}/parts-decision`, {
              parts_required: true,
            });
            setPartsDecision(null);
            if (data?.action === "create_pec") {
              setShowCreatePec(true);
            } else if (data?.action === "link_existing_pec" && data?.existing_pec?.id) {
              navigate(`/parts-requests/${data.existing_pec.id}`);
            }
          }}
          onNo={async () => {
            await api.post(`/warranty-claims/${id}/parts-decision`, { parts_required: false });
            setPartsDecision(null);
          }}
          onClose={() => setPartsDecision(null)}
        />
      )}

      {showCreatePec && (
        <NewPartsRequestDialog
          presetCaseId={swc.service_case_id}
          onClose={() => setShowCreatePec(false)}
          onCreated={() => {
            setShowCreatePec(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function WarrantyPartsDecisionDialog({ onYes, onNo, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="warranty-parts-decision-dialog"
    >
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8]">
          <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Próximo passo</div>
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">
            Esta reparação precisa de peças?
          </h2>
          <div className="text-xs text-[#6C757D] mt-0.5">
            A marca autorizou a reparação. Confirma se são necessárias peças
            para prosseguir com o serviço.
          </div>
        </div>
        <div className="p-5 text-sm text-[#495057] leading-relaxed">
          Se a reparação envolve substituir componentes, criamos uma PEC ligada
          a esta obra. Caso contrário (só mão-de-obra) o fluxo continua sem PEC.
        </div>
        <div className="px-5 pb-5 flex flex-col gap-2">
          <button
            onClick={onYes}
            className="w-full px-4 py-2.5 text-sm font-semibold bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm"
            data-testid="warranty-parts-decision-yes"
          >
            Sim — precisa de peças
          </button>
          <button
            onClick={onNo}
            className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 text-[#495057] hover:bg-[#F4F6F8] rounded-sm"
            data-testid="warranty-parts-decision-no"
          >
            Não — só mão-de-obra
          </button>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ swc, reload }) {
  const [form, setForm] = useState({
    brand_submission_ref: swc.brand_submission_ref || "",
    brand_response_notes: swc.brand_response_notes || "",
    approved_amount: swc.approved_amount || 0,
    invoice_reference: swc.invoice_reference || "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const dirty =
    form.brand_submission_ref !== (swc.brand_submission_ref || "") ||
    form.brand_response_notes !== (swc.brand_response_notes || "") ||
    Number(form.approved_amount) !== Number(swc.approved_amount || 0) ||
    form.invoice_reference !== (swc.invoice_reference || "");

  const save = async () => {
    setErr("");
    setBusy(true);
    try {
      const payload = {};
      if (form.brand_submission_ref !== (swc.brand_submission_ref || ""))
        payload.brand_submission_ref = form.brand_submission_ref || null;
      if (form.brand_response_notes !== (swc.brand_response_notes || ""))
        payload.brand_response_notes = form.brand_response_notes;
      if (Number(form.approved_amount) !== Number(swc.approved_amount || 0))
        payload.approved_amount = Number(form.approved_amount);
      if (form.invoice_reference !== (swc.invoice_reference || ""))
        payload.invoice_reference = form.invoice_reference || null;
      await api.patch(`/warranty-claims/${swc.id}`, payload);
      await reload();
    } catch (e) {
      setErr(formatError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {swc.service_case_id && (
        <SomContextBanner
          caseId={swc.service_case_id}
          testid="swc-detail-som-context"
        />
      )}

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white border border-gray-200 rounded-sm p-3">
        <Info label="Tipo de reclamação" value={swc.claim_type} testid="swc-detail-info-type" />
        <Info label="Submetida" value={fmtDateTime(swc.submitted_at)} />
        <Info
          label="Veículo · VIN"
          value={
            <>
              <span className="font-mono">{swc.vehicle_registration}</span>
              <div className="text-[10px] text-[#6C757D] font-mono">{swc.vin}</div>
            </>
          }
        />
        <Info label="Marca" value={swc.brand} />
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Descrição</div>
        <div className="bg-[#F4F6F8] rounded-sm p-3 text-sm" data-testid="swc-detail-description">
          {swc.description || "—"}
        </div>
      </section>

      <section className="space-y-3">
        <Field label="Referência de submissão à marca">
          <input
            value={form.brand_submission_ref}
            onChange={(e) => setForm({ ...form, brand_submission_ref: e.target.value })}
            className={inputCls}
            data-testid="swc-detail-submission-ref"
          />
        </Field>
        <Field label="Notas de resposta da marca">
          <textarea
            value={form.brand_response_notes}
            onChange={(e) => setForm({ ...form, brand_response_notes: e.target.value })}
            rows={3}
            className={inputCls}
            data-testid="swc-detail-response-notes"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor aprovado (MZN)">
            <input
              type="number"
              value={form.approved_amount}
              onChange={(e) => setForm({ ...form, approved_amount: e.target.value })}
              className={inputCls}
              data-testid="swc-detail-approved"
            />
          </Field>
          <Field label="Referência de factura">
            <input
              value={form.invoice_reference}
              onChange={(e) => setForm({ ...form, invoice_reference: e.target.value })}
              className={inputCls}
              data-testid="swc-detail-invoice"
            />
          </Field>
        </div>

        {err && (
          <div
            className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm"
            data-testid="swc-detail-save-error"
          >
            {err}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={busy || !dirty}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="swc-detail-save-btn"
          >
            <Send className="w-3 h-3" /> {busy ? "A gravar…" : "Gravar alterações"}
          </button>
        </div>
      </section>
    </div>
  );
}

function EvidencePanel({ swc, onChange }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/attachments?record_type=warranty_claim&record_id=${swc.id}`
      );
      setItems(data);
    } catch {
      /* noop */
    }
  }, [swc.id]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (file) => {
    setErr("");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("record_type", "warranty_claim");
    fd.append("record_id", swc.id);
    try {
      await api.post("/attachments/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      if (onChange) onChange();
    } catch (e) {
      setErr(formatError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-gray-200 rounded-sm p-3" data-testid="swc-detail-evidence">
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2 flex items-center gap-1">
        <FileText className="w-3 h-3" /> Evidências ({items.length})
      </div>
      {items.length === 0 && (
        <div className="text-xs text-[#6C757D] mb-2">
          Sem anexos. É necessário pelo menos um para submeter à marca.
        </div>
      )}
      <ul className="space-y-1 mb-2">
        {items.map((a) => (
          <li key={a.id} className="text-xs flex items-center justify-between">
            <span className="truncate">{a.original_filename}</span>
            <a
              href={`${api.defaults.baseURL}/attachments/${a.id}/download`}
              target="_blank"
              rel="noreferrer"
              className="text-[#1F3864] hover:underline ml-2"
            >
              ver
            </a>
          </li>
        ))}
      </ul>
      <label
        className={`inline-flex items-center gap-1.5 text-xs cursor-pointer border border-gray-300 hover:border-[#1F3864] rounded-sm px-2 py-1.5 ${
          busy ? "opacity-60 pointer-events-none" : ""
        }`}
        data-testid="swc-detail-evidence-upload-label"
      >
        <Upload className="w-3 h-3" /> {busy ? "A carregar…" : "Adicionar evidência"}
        <input
          type="file"
          onChange={(e) => e.target.files[0] && upload(e.target.files[0])}
          className="hidden"
          data-testid="swc-detail-evidence-upload"
        />
      </label>
      {err && (
        <div className="mt-2 px-2 py-1 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-xs">
          {err}
        </div>
      )}
    </section>
  );
}

function AuditTab({ recordType, recordId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get(`/audit-log?record_type=${recordType}&record_id=${recordId}`)
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [recordType, recordId]);

  if (loading) return <div className="text-xs text-[#6C757D]">A carregar…</div>;
  if (!rows.length) return <div className="text-xs text-[#6C757D]">Sem registos.</div>;
  return (
    <div
      className="bg-white border border-gray-200 rounded-sm overflow-hidden"
      data-testid="swc-detail-audit"
    >
      <table className="w-full text-sm">
        <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
          <tr>
            <th className="px-3 py-2 text-left">Timestamp</th>
            <th className="px-3 py-2 text-left">Utilizador</th>
            <th className="px-3 py-2 text-left">Ação</th>
            <th className="px-3 py-2 text-left">Comentário</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-100">
              <td className="px-3 py-2 text-xs text-[#6C757D] whitespace-nowrap">
                {fmtDateTime(r.timestamp)}
              </td>
              <td className="px-3 py-2 text-xs">{r.user_name || r.user_email || "—"}</td>
              <td className="px-3 py-2 text-xs font-semibold text-[#1F3864]">{r.action}</td>
              <td className="px-3 py-2 text-xs">{r.comment || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="text-sm text-[#1A1A2E]" data-testid={testid}>
        {value || "—"}
      </div>
    </div>
  );
}

const inputCls =
  "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";
